import { useEffect, useState } from 'react'
import { ShieldCheck, User, Crown, EyeOff } from 'lucide-react'

type DevAuthMode = 'anonymous' | 'free' | 'premium' | 'admin'

const DEV_AUTH_BYPASS =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&
  import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
const DEV_AUTH_MODE_KEY = 'optioneer_dev_auth_mode'

const MODES: Array<{
  mode: DevAuthMode
  label: string
  icon: typeof User
}> = [
  { mode: 'anonymous', label: 'Visitor', icon: EyeOff },
  { mode: 'free', label: 'Free', icon: User },
  { mode: 'premium', label: 'Premium', icon: Crown },
  { mode: 'admin', label: 'Admin', icon: ShieldCheck },
]

function readMode(): DevAuthMode {
  if (typeof window === 'undefined') {
    return 'premium'
  }

  const storedMode = window.localStorage.getItem(DEV_AUTH_MODE_KEY)
  return storedMode === 'anonymous' ||
    storedMode === 'free' ||
    storedMode === 'premium' ||
    storedMode === 'admin'
    ? storedMode
    : 'premium'
}

export default function DevAuthSwitcher() {
  const [mode, setMode] = useState<DevAuthMode>(() => readMode())

  useEffect(() => {
    if (!DEV_AUTH_BYPASS) {
      return
    }

    const handleModeChange = () => {
      setMode(readMode())
    }

    window.addEventListener('storage', handleModeChange)
    window.addEventListener('optioneer:dev-auth-mode-changed', handleModeChange)

    return () => {
      window.removeEventListener('storage', handleModeChange)
      window.removeEventListener('optioneer:dev-auth-mode-changed', handleModeChange)
    }
  }, [])

  if (!DEV_AUTH_BYPASS) {
    return null
  }

  const updateMode = (nextMode: DevAuthMode) => {
    window.localStorage.setItem(DEV_AUTH_MODE_KEY, nextMode)
    window.dispatchEvent(new Event('optioneer:dev-auth-mode-changed'))
  }

  return (
    <div className="fixed bottom-4 right-4 z-[90] rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
      <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        Dev Auth
      </div>
      <div className="flex items-center gap-1">
        {MODES.map((item) => {
          const Icon = item.icon
          const isActive = mode === item.mode

          return (
            <button
              key={item.mode}
              type="button"
              onClick={() => updateMode(item.mode)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition ${
                isActive
                  ? 'bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{item.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
